import React, { useEffect, useState } from "react";

type Entry = {
  sys: {
    id: string;
    contentType?: { sys: { id: string } };
    [key: string]: any;
  };
  fields: any;
};

type PrepaidPlansData = {
  items: Array<{ fields: any }>;
  includes: { Entry: Entry[] };
};

function renderRichText(richText: any): React.ReactNode {
  // Simple render for Contentful rich text (only top-level paragraphs and text)
  if (!richText || !richText.content) return null;
  return richText.content.map((node: any, i: number) => {
    if (node.nodeType === "paragraph") {
      return (
        <p key={i}>
          {node.content.map((child: any, j: number) => child.value || "")}
        </p>
      );
    }
    return null;
  });
}

// Categorization logic based on entryTitle or fieldText
function categorizePlan(entry: Entry): string {
  const text = (entry.fields.entryTitle || entry.fields.fieldText || "").toLowerCase();
  if (text.includes('booster') || text.includes('addon')) return 'Booster Add-ons';
  if (text.includes('360')) return '360 Day Plans';
  return '4G Base Plans';
}

function getFeatures(entry: Entry, data?: PrepaidPlansData): React.ReactNode[] {
  if (!data) return [];
  const entryMap = new Map(data.includes.Entry.map(e => [e.sys.id, e]));
  // Collect unique refs by id
  const seen = new Set<string>();
  const refs: any[] = [];
  Object.keys(entry.fields).forEach(key => {
    if (key.startsWith('fieldReferenceComponentRichText')) {
      const items = entry.fields[key];
      const list = items == null ? [] : Array.isArray(items) ? items : [items];
      list.forEach(item => {
        const id = item.sys?.id;
        if (id) {
          if (!seen.has(id)) {
            seen.add(id);
            refs.push(item);
          }
        } else {
          refs.push(item);
        }
      });
    }
  });
  // Map unique refs to feature content
  return refs.flatMap((item: any) => {
    if (item.fields?.fieldText) return [item.fields.fieldText];
    if (item.fields?.fieldRichText) return [renderRichText(item.fields.fieldRichText)];
    const linked = entryMap.get(item.sys?.id);
    if (linked) {
      if (linked.fields.fieldText) return [linked.fields.fieldText];
      if (linked.fields.fieldRichText) return [renderRichText(linked.fields.fieldRichText)];
    }
    return [];
  });
}

// Extract numeric price from entry's title/fieldText; return Infinity if no price
function getPrice(entry: Entry): number {
  const text = entry.fields.entryTitle || entry.fields.fieldText || "";
  const match = text.match(/\$([0-9]+)(?:\.(\d{2}))?/);
  if (!match) return Infinity;
  const dollars = parseInt(match[1], 10);
  const cents = match[2] ? parseInt(match[2], 10) : 0;
  return dollars + cents / 100;
}

const TABS = ["4G Base Plans", "360 Day Plans", "Booster Add-ons"];

export default function PrepaidPlans() {
  const [planEntries, setPlanEntries] = useState<Entry[]>([]);
  const [rawData, setRawData] = useState<PrepaidPlansData>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [openAccordions, setOpenAccordions] = useState<{ [tab: number]: string | null }>({});
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetch("/data/prepaidPlans.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch plans");
        return res.json();
      })
      .then((data: PrepaidPlansData) => {
        setRawData(data);
        // Only include blockProductTextCta entries
        const filtered = (data.includes?.Entry || []).filter(
          entry => entry.sys.contentType?.sys.id === 'blockProductTextCta'
        );
        // Separate boosters (skip dedupe) from base & 360
        const nonBoosters = filtered.filter(e => categorizePlan(e) !== 'Booster Add-ons');
        const boosters = filtered.filter(e => categorizePlan(e) === 'Booster Add-ons');
        // Deduplicate only non-boosters by title
        const uniqueNonBoosters = Array.from(
          new Map(
            nonBoosters.map(e => [
              (e.fields.entryTitle || e.fields.fieldText).trim(),
              e
            ])
          ).values()
        );
        // Combine and sort all entries by price ascending
        const allEntries = [...uniqueNonBoosters, ...boosters];
        allEntries.sort((a, b) => getPrice(a) - getPrice(b));
        setPlanEntries(allEntries);
        // Open first accordion per tab based on combined list
        const grouped = TABS.map(tab => allEntries.find(e => categorizePlan(e) === tab)?.sys.id || null);
        setOpenAccordions(grouped.reduce((acc, id, idx) => ({ ...acc, [idx]: id }), {}));
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const groupedPlans = TABS.map(tab =>
    planEntries.filter(e => {
      if (categorizePlan(e) !== tab) return false;
      if (tab === '4G Base Plans') {
        const price = getPrice(e);
        const title = (e.fields.entryTitle || e.fields.fieldText || '') as string;
        // remove unwanted prices and QC $19 plan
        if ([22, 27, 32].includes(price) || title.includes('QC')) return false;
      }
      return true;
    })
  );

  const handleAccordion = (tabIdx: number, id: string) => {
    setOpenAccordions((prev) => ({ ...prev, [tabIdx]: prev[tabIdx] === id ? null : id }));
  };

  const toggleCard = (id: string) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));

  // --- Extract intro/availability text from the JSON data ---
  const [introText, setIntroText] = useState<string>("");
  useEffect(() => {
    fetch("/data/prepaidPlans.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch plans");
        return res.json();
      })
      .then((data: PrepaidPlansData) => {
        // Extract intro/availability text
        setIntroText(data.items[0]?.fields?.fieldText2 || "");
      })
      .catch(() => {});
  }, []);

  if (loading) return <div>Loading prepaid plans...</div>;
  if (error) return <div className="text-red-500">Error: {error}</div>;

  return (
    <section className="my-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Optional: Available notice at the top */}
        <div className="mb-6">
          <div className="text-lg font-semibold text-primary">Available: TELUS Prepaid Talk & Text 100. Visit a store for more details.</div>
        </div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <img src="/images/koodo-logo.png" alt="Koodo Logo" className="h-10 mr-3" />
            <h2 className="text-2xl font-bold text-primary">Prepaid Plans</h2>
          </div>
          <a href="tel:+16472002040" className="text-xl font-bold text-white bg-primary px-4 py-2 rounded-lg shadow hover:bg-primary-dark transition duration-200">
            Call us: +1-647-200-2040
          </a>
        </div>
        {introText && (
          <div className="mb-4 text-base text-gray-800 bg-blue-50 border border-blue-100 rounded px-4 py-2">
            {introText}
          </div>
        )}
        {/* Tabs */}
        <div className="flex mb-4 border-b">
          {TABS.map((tab, idx) => (
            <button
              key={tab}
              className={`px-4 py-2 font-semibold border-b-2 transition-colors duration-200 ${activeTab === idx ? "border-blue-600 text-blue-600" : "border-transparent text-gray-600 hover:text-blue-500"}`}
              onClick={() => setActiveTab(idx)}
              aria-selected={activeTab === idx}
              role="tab"
              tabIndex={0}
            >
              {tab}
            </button>
          ))}
        </div>
        {/* Tab Panels */}
        <div role="tabpanel">
          {groupedPlans[activeTab].length === 0 && (
            <div className="text-gray-500">No plans found for this category.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groupedPlans[activeTab].map(entry => {
              const rawText = entry.fields.entryTitle || entry.fields.fieldText || "";
              const title = rawText;
              const features = getFeatures(entry, rawData);
              const category = categorizePlan(entry);
              const isExpanded = expandedCards[entry.sys.id] || false;
              const displayFeatures = isExpanded ? features : features.slice(0, 5);
              if (category === 'Booster Add-ons') {
                return (
                  <div key={entry.sys.id} className="rounded-xl border bg-white shadow-lg flex flex-col p-6 relative">
                    <button className="absolute top-2 right-2 bg-primary text-white px-2 py-1 rounded text-xs font-semibold">Select Plan</button>
                    <h3 className="text-xl font-semibold mb-2">{title}</h3>
                    <div className="mt-4 text-sm text-primary font-semibold flex flex-wrap gap-4">
                      {displayFeatures.length > 0
                        ? displayFeatures.map((feat, i) => (
                            <div key={i} className="inline-flex items-center mr-4">
                              <span className="mr-1">•</span>
                              <span>{feat}</span>
                            </div>
                          ))
                        : <div>No features listed.</div>}
                      {features.length > 5 && (
                        <button onClick={() => toggleCard(entry.sys.id)} className="mt-2 text-blue-600 underline text-sm">
                          {isExpanded ? 'Show less' : 'View more'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
              const priceMatch = rawText.match(/^\$([0-9]{2,})(?:\.(\d{2}))?/);
              if (!priceMatch) return null;
              const price = priceMatch[1];
              const priceCents = priceMatch[2] || "00";
              return (
                <div key={entry.sys.id} className="rounded-xl border bg-white shadow-lg flex flex-col p-6 relative">
                  <button className="absolute top-2 right-2 bg-primary text-white px-2 py-1 rounded text-xs font-semibold">Select Plan</button>
                  <div className="mb-2 flex items-baseline">
                    <span className="text-4xl font-extrabold" style={{ color: 'hsl(338, 83%, 55%)' }}>${price}</span>
                    <span className="text-2xl align-super" style={{ color: 'hsl(338, 83%, 55%)' }}>.{priceCents}</span>
                    <span className="ml-2 text-base text-gray-500 font-medium">per 30 days</span>
                  </div>
                  <div className="mt-4 text-sm text-primary font-semibold flex flex-wrap gap-4">
                    {displayFeatures.length > 0
                      ? displayFeatures.map((feat, i) => (
                          <div key={i} className="inline-flex items-center mr-4">
                            <span className="mr-1">•</span>
                            <span>{feat}</span>
                          </div>
                        ))
                      : <div>No features listed.</div>}
                    {features.length > 5 && (
                      <button onClick={() => toggleCard(entry.sys.id)} className="mt-2 text-blue-600 underline text-sm">
                        {isExpanded ? 'Show less' : 'View more'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}